import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CircleDollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Session } from "../../features/sessions/models/Session";
import { buildAttendanceRoster } from "../../features/sessions/utils/buildAttendanceRoster";
import { selectMembers, selectGroups } from "../../features/members/members-slice";
import { MemberBillingIssues } from "../../features/billing/models/MemberBillingIssues";
import { fetchMemberIssues } from "../../features/billing/slice-extensions/billing-slice-extension";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { ROUTES } from "../../routes";

interface SessionBillingSummaryProps {
  session: Session;
}

/**
 * Billing standing across the session's participants: either "no issues" or the
 * members with overdue payments / no active contract, each linking to their
 * billing page.
 */
const SessionBillingSummary: React.FC<SessionBillingSummaryProps> = ({ session }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Billing signals are an Owner/Admin concern — plain Instructors never see them.
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const members = useAppSelector(selectMembers);
  const groups = useAppSelector(selectGroups);
  const [issues, setIssues] = useState<MemberBillingIssues[] | null>(null);

  const roster = useMemo(
    () => buildAttendanceRoster(session, members, groups),
    [session, members, groups]
  );
  const rosterKey = roster.memberIds.slice().sort().join(",");
  const nameById = useMemo(
    () => new Map(roster.sections.flatMap((s) => s.members).map((m) => [m.memberId, m.fullName])),
    [roster]
  );

  useEffect(() => {
    if (!isOrgOwnerOrAdmin) {
      return;
    }
    if (!rosterKey) {
      setIssues([]);
      return;
    }
    let cancelled = false;
    dispatch(fetchMemberIssues(rosterKey.split(",")))
      .unwrap()
      .then((result) => { if (!cancelled) setIssues(result); })
      .catch(() => { if (!cancelled) setIssues(null); });
    return () => { cancelled = true; };
  }, [dispatch, rosterKey, isOrgOwnerOrAdmin]);

  if (!isOrgOwnerOrAdmin) return null;
  if (roster.totalCount === 0) return null;

  const problematic = (issues ?? []).filter(
    (i) => i.overduePaymentsCount > 0 || !i.hasActiveContract
  );

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex items-center gap-2">
        <CircleDollarSign size={20} className="text-grey-30 dark:text-grey-70" />
        <h3>{t("sessions.billing.title")}</h3>
      </div>

      {issues === null ? (
        <p className="mt-3 text-sm text-grey-40 dark:text-grey-60">
          {t("sessions.billing.unavailable")}
        </p>
      ) : problematic.length === 0 ? (
        <p className="mt-3 text-sm text-green-700 dark:text-green-400">
          {t("sessions.billing.noIssues")}
        </p>
      ) : (
        <div className="mt-3 space-y-1">
          {problematic.map((i) => (
            <div
              key={i.memberId}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 hover:bg-grey-95 dark:hover:bg-primary-20"
            >
              <Link
                to={ROUTES.MEMBER_BILLING.replace(":id", i.memberId)}
                className="text-sm font-medium text-primary-40 hover:underline dark:text-primary-90"
              >
                {nameById.get(i.memberId) ?? t("sessions.billing.member")}
              </Link>
              <span className="flex flex-wrap gap-1 text-xs">
                {i.overduePaymentsCount > 0 && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                    {t("sessions.billing.overdue", { count: i.overduePaymentsCount })}
                  </span>
                )}
                {!i.hasActiveContract && (
                  <span className="rounded-full bg-grey-95 px-2 py-0.5 font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
                    {t("sessions.billing.noContract")}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionBillingSummary;
